'use client';

import { cn, format, isArrayItem } from '@core/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import {
  forwardRef,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import SvgDelete from '../../../__generated__/icon/SvgDelete';
import type { HTMLTagProps } from '../../../types';
import { InputDecoratorContext } from '../InputDecorator/InputDecorator';
import styles from './Input.module.scss';

const inputVariants = cva(
  'relative flex items-center justify-center border-1 border-Gray-300 bg-Gray-white text-Gray-900 outline-none transition-colors duration-150 typo-body3',
  {
    variants: {
      dimensions: {
        H40: 'h-40',
        H48: 'h-48',
        H56: 'h-56',
      },
      roundness: {
        rectangle: 'rounded-xsmall',
        capsule: 'rounded-pill',
      },
    },
    defaultVariants: {
      dimensions: 'H48',
      roundness: 'rectangle',
    },
  },
);

export interface InputProps
  extends
    Omit<HTMLTagProps<'input'>, 'type'>,
    VariantProps<typeof inputVariants> {
  leftContent?: React.ReactNode;
  leftContentClassName?: string;
  rightContent?: React.ReactNode;
  type?: HTMLTagProps<'input'>['type'] | 'local-string-number' | 'text-date';
  invalid?: boolean;
  onClear?: () => void;
}

const CUSTOM_FORMAT_TYPE = ['local-string-number', 'text-date', 'tel'] as const;

const formatValue = (
  value: string,
  type: (typeof CUSTOM_FORMAT_TYPE)[number],
) => {
  switch (type) {
    case 'local-string-number': {
      try {
        return value ? BigInt(value).toLocaleString() : value;
      } catch {
        return value;
      }
    }

    case 'tel': {
      return format.phone(value);
    }

    case 'text-date': {
      const textDate = value.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');

      return Number.isNaN(new Date(textDate).getTime()) ? value : textDate;
    }
  }
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      leftContent,
      leftContentClassName,
      rightContent,
      className,
      dimensions,
      type = 'text',
      onBlur,
      onChange,
      onInvalid,
      onFocus,
      invalid,
      id,
      inputMode,
      onKeyDown,
      onClear,
      ...restProps
    },
    externalRef,
  ) => {
    const ref = useRef<HTMLInputElement>(null);

    useImperativeHandle(externalRef, () => ref.current as HTMLInputElement, []);

    const [isFocused, setIsFocused] = useState(false);

    const formattedValue = (() => {
      if (!isArrayItem(CUSTOM_FORMAT_TYPE, type)) return '';

      const currentValue =
        (restProps.value as string) ?? ref.current?.value ?? '';

      return formatValue(currentValue, type);
    })();

    const isDisplayFormattedValue = !isFocused && formattedValue;

    const inputDecoratorContext = useContext(InputDecoratorContext);
    const hasError = invalid ?? !!inputDecoratorContext?.validationMessage;

    const customType = (() => {
      switch (type) {
        case 'local-string-number':
        case 'text-date':
          return 'text';

        case 'tel':
          return 'tel';

        case 'button':
          return (restProps.value ?? ref.current?.value) ? 'button' : 'text';

        default:
          return type;
      }
    })();

    const isButton = type === 'button';

    const displayId =
      id ??
      (typeof inputDecoratorContext?.label === 'string'
        ? inputDecoratorContext?.label
        : undefined);

    return (
      <div
        className={cn(
          styles['input-container'],
          inputVariants({
            dimensions,
          }),
          {
            'cursor-pointer': isButton,
            'border-R-500': hasError,
          },
          'text-start has-[input:focus]:border-Gray-900',
          'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-Gray-200 has-[input:disabled]:text-Gray-500',
          className,
        )}
        role="presentation"
        onClick={() => {
          ref.current?.focus();
          ref.current?.click();
        }}
      >
        <div
          className={cn(
            'relative size-fit min-w-fit max-w-fit text-nowrap pl-16',
            {
              'pr-8': leftContent,
            },
            leftContentClassName,
          )}
        >
          {leftContent}
        </div>
        {isDisplayFormattedValue ? (
          <div className="w-full min-w-0 flex-1 bg-transparent text-inherit">
            {formattedValue}
          </div>
        ) : null}
        <input
          //* 검증 실패·도움말을 보조기술에 연결한다. 호출부가 직접 준 값이 있으면 그쪽이 이긴다(restProps가 뒤).
          aria-invalid={hasError || undefined}
          aria-describedby={inputDecoratorContext?.messageId}
          {...restProps}
          ref={ref}
          className={cn(
            styles.input,
            'truncate',
            'w-full min-w-0 text-start',
            'flex-1 bg-transparent outline-none',
            {
              'caret-transparent': isButton,
              'absolute size-full left-0 right-0 opacity-0':
                isDisplayFormattedValue,
            },
            {
              'typo-body4 font-400': dimensions === 'H40',
            },
          )}
          id={displayId}
          inputMode={customType === 'number' ? 'numeric' : inputMode}
          type={customType}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          onChange={(e) => {
            let clearValue = e.target.value;

            switch (type) {
              case 'local-string-number': {
                //* 숫자형 문자열(local-string-number)일 때는 숫자 이외의 문자를 제거
                clearValue = clearValue.replaceAll(/\D/g, '');
                break;
              }

              case 'text-date': {
                clearValue = clearValue.slice(0, 8);
                break;
              }

              case 'tel': {
                const digits = clearValue.replaceAll(/\D/g, '');
                clearValue = digits.slice(0, 11);
                break;
              }
            }

            e.target.value = clearValue;

            onChange?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onInvalid={(e) => {
            e.preventDefault();
            onInvalid?.(e);
          }}
          onKeyDown={(e) => {
            if (e.keyCode === 229) return;
            onKeyDown?.(e);
          }}
        />
        {customType === 'search' ? (
          <button
            aria-label="검색어 초기화"
            className="hidden size-16 items-center justify-center rounded-pill bg-Gray-400"
            type="button"
            onClick={() => {
              if (ref.current?.value) {
                ref.current.value = '';
              }

              onClear?.();
            }}
          >
            <SvgDelete
              className="size-9 stroke-Gray-white text-Gray-white"
              name="delete"
              strokeWidth={2}
            />
          </button>
        ) : null}
        <div
          className={cn(
            'relative size-fit min-w-fit max-w-fit text-nowrap pr-16',
            {
              'pl-8': rightContent,
            },
          )}
        >
          {rightContent}
        </div>
      </div>
    );
  },
);

Input.displayName = 'Input';
